import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  adminMe, adminStats, adminBookings, adminUpdateBooking,
  adminListPin, adminCreatePin, adminUpdatePin, adminDeletePin,
  adminListTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  adminCreateFaq, adminUpdateFaq, adminDeleteFaq,
  adminCreatePrice, adminUpdatePrice, adminDeletePrice,
  adminCustomers, adminGetSettings, adminSaveSettings,
  getFaqs, getPrices,
} from "../../api";
import { LogOut, Loader2, LayoutDashboard, Ticket, MapPin, Tag, MessageSquare, HelpCircle, Users, Plus, Trash2, Save, X, Settings as SettingsIcon, Image as ImageIcon } from "lucide-react";
import { jobStatuses, applianceMeta } from "../../constants";
import SEO from "../../components/SEO";
import { useToast } from "../../hooks/use-toast";

const tabs = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: Ticket },
  { id: "customers", label: "Customers", icon: Users },
  { id: "pincodes", label: "PIN Codes", icon: MapPin },
  { id: "prices", label: "Prices", icon: Tag },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [user, setUser] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("revive_admin_token");
    if (!t) { nav("/admin/login"); return; }
    adminMe().then((r) => { setUser(r.username); setLoading(false); }).catch(() => { localStorage.removeItem("revive_admin_token"); nav("/admin/login"); });
  }, [nav]);

  const logout = () => { localStorage.removeItem("revive_admin_token"); localStorage.removeItem("revive_admin_user"); nav("/admin/login"); };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#e30613]" /></div>;

  return (
    <div className="min-h-screen bg-neutral-50">
      <SEO title="Admin | Revive Solutions" description="Admin panel" />
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#e30613] text-white grid place-items-center font-bold rounded-md">R</div>
            <div className="leading-tight"><div className="font-display font-semibold">Revive Solutions</div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Admin Panel</div></div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="text-sm text-slate-600 hover:text-[#0f172a]">View Website</Link>
            <span className="text-sm text-slate-500 hidden md:block">Signed in as <b>{user}</b></span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[#e30613] font-semibold"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        <aside className="bg-white border border-neutral-200 rounded-lg p-3 h-max sticky top-6">
          <nav className="flex lg:flex-col overflow-x-auto no-scrollbar gap-1">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap ${tab === t.id ? "bg-[#e30613] text-white" : "text-slate-600 hover:bg-neutral-100"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main>
          {tab === "dashboard" && <DashboardTab />}
          {tab === "bookings" && <BookingsTab />}
          {tab === "customers" && <CustomersTab />}
          {tab === "pincodes" && <PinCodesTab />}
          {tab === "prices" && <PricesTab />}
          {tab === "testimonials" && <TestimonialsTab />}
          {tab === "faqs" && <FaqsTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

// ================== Dashboard ==================
function DashboardTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminStats().then(setStats).catch(() => {}); }, []);
  if (!stats) return <div className="grid place-items-center h-40"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const cards = [
    { label: "New Leads", value: stats.new_leads, color: "bg-[#e30613]" },
    { label: "Today's Bookings", value: stats.today_bookings, color: "bg-blue-600" },
    { label: "In Progress", value: stats.in_progress, color: "bg-amber-500" },
    { label: "Completed", value: stats.completed, color: "bg-emerald-600" },
    { label: "Pending Payments", value: stats.pending_payments, color: "bg-purple-600" },
    { label: "Total Bookings", value: stats.total, color: "bg-slate-700" },
  ];
  return (
    <div>
      <h2 className="font-display text-2xl mb-1">Dashboard</h2>
      <p className="text-slate-500 text-sm mb-6">A quick view of your service business today.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-neutral-200 rounded-lg p-5">
            <div className={`w-2 h-2 rounded-full ${c.color} mb-2`} />
            <div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="font-display text-3xl font-medium mt-1">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-neutral-200 rounded-lg p-5">
          <div className="font-semibold mb-3">Top Appliances</div>
          {(stats.top_appliances || []).length === 0 ? <div className="text-sm text-slate-500">No bookings yet.</div> : (
            <ul className="space-y-2 text-sm">{stats.top_appliances.map((a) => (<li key={a._id} className="flex items-center justify-between"><span>{applianceMeta[a._id]?.label || a._id}</span><b>{a.count}</b></li>))}</ul>
          )}
        </div>
        <div className="bg-white border border-neutral-200 rounded-lg p-5">
          <div className="font-semibold mb-3">Top PIN Codes</div>
          {(stats.top_pincodes || []).length === 0 ? <div className="text-sm text-slate-500">No bookings yet.</div> : (
            <ul className="space-y-2 text-sm">{stats.top_pincodes.map((p) => (<li key={p._id} className="flex items-center justify-between"><span>{p._id}</span><b>{p.count}</b></li>))}</ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ================== Bookings ==================
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const load = () => { setLoading(true); adminBookings(filter).then((r) => { setBookings(r); setLoading(false); }); };
  useEffect(load, [filter]);

  const update = async (id, patch) => {
    await adminUpdateBooking(id, patch);
    toast({ title: "Booking updated" });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div><h2 className="font-display text-2xl">Bookings</h2><p className="text-slate-500 text-sm">Manage all customer service requests.</p></div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="field w-56"><option value="">All statuses</option>{jobStatuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>
      {loading ? <div className="h-40 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : bookings.length === 0 ? (
        <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-10 text-center text-slate-500">No bookings yet.</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="table-clean w-full"><thead><tr><th>Job No.</th><th>Customer</th><th>Appliance</th><th>Date / Slot</th><th>PIN</th><th>Status</th></tr></thead>
            <tbody>{bookings.map((b) => (
              <React.Fragment key={b.id}>
                <tr onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="cursor-pointer">
                  <td className="font-mono font-semibold">{b.job_no}</td>
                  <td><div className="font-semibold">{b.customer_name}</div><div className="text-xs text-slate-500">+91 {b.mobile}</div></td>
                  <td>{applianceMeta[b.appliance]?.label || b.appliance}<div className="text-xs text-slate-500">{b.service}</div></td>
                  <td>{b.preferred_date}<div className="text-xs text-slate-500">{b.preferred_slot}</div></td>
                  <td>{b.pincode}</td>
                  <td><span className="badge-red">{b.status}</span></td>
                </tr>
                {expanded === b.id && (
                  <tr><td colSpan={6} className="bg-neutral-50">
                    <div className="p-4 grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs uppercase text-slate-500 mb-1">Address</div>
                        <div className="text-sm mb-3">{b.address}</div>
                        <div className="text-xs uppercase text-slate-500 mb-1">Problem</div>
                        <div className="text-sm mb-3">{b.problem_description || "—"}</div>
                        <div className="text-xs uppercase text-slate-500 mb-1">Email</div>
                        <div className="text-sm">{b.email || "—"}</div>
                        {b.photos && b.photos.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs uppercase text-slate-500 mb-1">Photos</div>
                            <div className="flex gap-2 flex-wrap">
                              {b.photos.map((src, i) => (
                                <a key={i} href={src} target="_blank" rel="noreferrer">
                                  <img src={src} alt={`photo ${i+1}`} className="w-16 h-16 object-cover rounded border border-neutral-200" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {b.payment_status === "paid" && (
                          <div className="mt-3 inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Paid · {b.payment_txn_id}</div>
                        )}
                      </div>
                      <div>
                        <label className="field-label">Change Status</label>
                        <select defaultValue={b.status} onChange={(e) => update(b.id, { status: e.target.value })} className="field mb-3">
                          {jobStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="field-label">Admin Notes</label>
                        <textarea defaultValue={b.admin_notes} onBlur={(e) => update(b.id, { admin_notes: e.target.value })} className="field resize-none" rows="2"></textarea>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ================== Customers ==================
function CustomersTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { adminCustomers().then(setItems); }, []);
  return (
    <div>
      <h2 className="font-display text-2xl mb-1">Customers</h2>
      <p className="text-slate-500 text-sm mb-6">All customers who have booked at least once.</p>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="table-clean w-full"><thead><tr><th>Name</th><th>Mobile</th><th>Email</th><th>PIN</th><th>Address</th></tr></thead>
          <tbody>{items.length === 0 ? <tr><td colSpan={5} className="text-center text-slate-500 py-8">No customers yet.</td></tr> : items.map((c) => (
            <tr key={c.id}><td className="font-semibold">{c.name}</td><td>+91 {c.mobile}</td><td>{c.email || "—"}</td><td>{c.pincode}</td><td className="max-w-md">{c.address}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ================== Generic CRUD tab ==================
function CrudTab({ title, subtitle, load, create, update, del, columns, blankItem, renderForm, renderRow }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blankItem);
  const { toast } = useToast();

  const refresh = () => load().then(setItems);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const startCreate = () => { setForm(blankItem); setCreating(true); setEditing(null); };
  const startEdit = (it) => { setForm(it); setEditing(it.id); setCreating(false); };
  const save = async () => {
    if (editing) await update(editing, form); else await create(form);
    toast({ title: "Saved" }); setEditing(null); setCreating(false); setForm(blankItem); refresh();
  };
  const remove = async (id) => { if (!window.confirm("Delete this item?")) return; await del(id); toast({ title: "Deleted" }); refresh(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><h2 className="font-display text-2xl">{title}</h2><p className="text-slate-500 text-sm">{subtitle}</p></div>
        <button onClick={startCreate} className="btn-primary"><Plus className="w-4 h-4" /> Add</button>
      </div>
      {(creating || editing) && (
        <div className="bg-white border border-neutral-200 rounded-lg p-5 mb-4">
          <div className="flex items-center justify-between mb-4"><div className="font-semibold">{editing ? "Edit" : "New"}</div><button onClick={() => { setEditing(null); setCreating(false); }} className="text-slate-500"><X className="w-4 h-4" /></button></div>
          {renderForm(form, setForm)}
          <div className="mt-4"><button onClick={save} className="btn-primary"><Save className="w-4 h-4" /> Save</button></div>
        </div>
      )}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="table-clean w-full"><thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}<th className="text-right">Actions</th></tr></thead>
          <tbody>{items.length === 0 ? <tr><td colSpan={columns.length + 1} className="text-center text-slate-500 py-8">No items yet.</td></tr> : items.map((it) => (
            <tr key={it.id}>{renderRow(it)}<td className="text-right whitespace-nowrap"><button onClick={() => startEdit(it)} className="text-slate-600 hover:text-[#e30613] text-sm font-semibold mr-4">Edit</button><button onClick={() => remove(it.id)} className="text-[#e30613]"><Trash2 className="w-4 h-4" /></button></td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function PinCodesTab() {
  return <CrudTab title="PIN Codes" subtitle="Manage the areas you service." load={adminListPin} create={adminCreatePin} update={adminUpdatePin} del={adminDeletePin} columns={["PIN", "Area", "Active"]} blankItem={{ pincode: "", area: "", active: true }}
    renderForm={(f, setF) => (
      <div className="grid md:grid-cols-3 gap-4">
        <div><label className="field-label">PIN Code</label><input value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/[^0-9]/g, "").slice(0, 6) })} className="field" /></div>
        <div><label className="field-label">Area</label><input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="field" /></div>
        <div><label className="field-label">Active</label><select value={f.active ? "1" : "0"} onChange={(e) => setF({ ...f, active: e.target.value === "1" })} className="field"><option value="1">Yes</option><option value="0">No</option></select></div>
      </div>
    )}
    renderRow={(it) => (<><td className="font-mono font-semibold">{it.pincode}</td><td>{it.area}</td><td>{it.active ? "Yes" : "No"}</td></>)}
  />;
}

function PricesTab() {
  return <CrudTab title="Service Prices" subtitle="Manage the pricing table shown on the website." load={getPrices} create={adminCreatePrice} update={adminUpdatePrice} del={adminDeletePrice} columns={["Service", "Starting Price", "Order"]} blankItem={{ label: "", starting_price: "", order: 0 }}
    renderForm={(f, setF) => (
      <div className="grid md:grid-cols-3 gap-4">
        <div><label className="field-label">Service</label><input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className="field" /></div>
        <div><label className="field-label">Starting price</label><input value={f.starting_price} onChange={(e) => setF({ ...f, starting_price: e.target.value })} className="field" placeholder="e.g. ₹1,000 + GST" /></div>
        <div><label className="field-label">Order</label><input type="number" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value) || 0 })} className="field" /></div>
      </div>
    )}
    renderRow={(it) => (<><td className="font-medium">{it.label}</td><td className="font-semibold">{it.starting_price}</td><td>{it.order}</td></>)}
  />;
}

function TestimonialsTab() {
  return <CrudTab title="Testimonials" subtitle="Add real customer reviews. Do not fabricate." load={adminListTestimonials} create={adminCreateTestimonial} update={adminUpdateTestimonial} del={adminDeleteTestimonial} columns={["Name", "Area", "Rating", "Published"]} blankItem={{ name: "", area: "", rating: 5, text: "", published: true }}
    renderForm={(f, setF) => (
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className="field-label">Name</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="field" /></div>
        <div><label className="field-label">Area</label><input value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="field" /></div>
        <div><label className="field-label">Rating</label><select value={f.rating} onChange={(e) => setF({ ...f, rating: parseInt(e.target.value) })} className="field">{[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} ★</option>)}</select></div>
        <div><label className="field-label">Published</label><select value={f.published ? "1" : "0"} onChange={(e) => setF({ ...f, published: e.target.value === "1" })} className="field"><option value="1">Yes</option><option value="0">No</option></select></div>
        <div className="md:col-span-2"><label className="field-label">Review Text</label><textarea rows="3" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="field resize-none"></textarea></div>
      </div>
    )}
    renderRow={(it) => (<><td className="font-semibold">{it.name}</td><td>{it.area}</td><td>{it.rating} ★</td><td>{it.published ? "Yes" : "No"}</td></>)}
  />;
}

function FaqsTab() {
  return <CrudTab title="FAQs" subtitle="Edit frequently asked questions." load={getFaqs} create={adminCreateFaq} update={adminUpdateFaq} del={adminDeleteFaq} columns={["Question", "Order"]} blankItem={{ question: "", answer: "", order: 0 }}
    renderForm={(f, setF) => (
      <div className="grid gap-4">
        <div><label className="field-label">Question</label><input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} className="field" /></div>
        <div><label className="field-label">Answer</label><textarea rows="4" value={f.answer} onChange={(e) => setF({ ...f, answer: e.target.value })} className="field resize-none"></textarea></div>
        <div className="md:w-40"><label className="field-label">Order</label><input type="number" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value) || 0 })} className="field" /></div>
      </div>
    )}
    renderRow={(it) => (<><td className="font-medium">{it.question}</td><td>{it.order}</td></>)}
  />;
}


function SettingsTab() {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", razorpay_enabled: false, razorpay_mode: "test" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminGetSettings().then((d) => { setS(d); setForm({ razorpay_key_id: d.razorpay_key_id || "", razorpay_key_secret: "", razorpay_enabled: !!d.razorpay_enabled, razorpay_mode: d.razorpay_mode || "test" }); }); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { razorpay_key_id: form.razorpay_key_id, razorpay_enabled: form.razorpay_enabled, razorpay_mode: form.razorpay_mode };
      if (form.razorpay_key_secret) payload.razorpay_key_secret = form.razorpay_key_secret;
      await adminSaveSettings(payload);
      toast({ title: "Settings saved", description: "Payment configuration updated." });
      const d = await adminGetSettings(); setS(d); setForm((prev) => ({ ...prev, razorpay_key_secret: "" }));
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "Try again" }); }
    finally { setSaving(false); }
  };
  if (!s) return <div className="h-40 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div>
      <h2 className="font-display text-2xl mb-1">Settings</h2>
      <p className="text-slate-500 text-sm mb-6">Configure online payments and other website settings.</p>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-md bg-[#fef2f2] text-[#e30613] grid place-items-center"><SettingsIcon className="w-4 h-4" /></div>
          <div><div className="font-semibold">Razorpay (Online Payments)</div><div className="text-xs text-slate-500">UPI / Cards / Net-banking via Razorpay</div></div>
        </div>
        <p className="text-sm text-slate-500 mb-5 mt-3">Paste your Razorpay Key ID and Key Secret. Keep Test mode ON while testing. When ready to go live, generate Live keys from Razorpay dashboard, paste them, and switch mode to Live.</p>

        <div className="grid gap-4">
          <div><label className="field-label">Razorpay Key ID</label><input value={form.razorpay_key_id} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })} className="field font-mono" placeholder="rzp_test_XXXXXXXXXXXXXX" /></div>
          <div>
            <label className="field-label">Razorpay Key Secret</label>
            <input value={form.razorpay_key_secret} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })} className="field font-mono" placeholder={s.razorpay_key_secret_masked ? `Saved: ${s.razorpay_key_secret_masked} (leave blank to keep)` : "Paste secret"} />
            <div className="text-xs text-slate-500 mt-1">The secret is stored server-side and never shown again after saving.</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="field-label">Mode</label><select value={form.razorpay_mode} onChange={(e) => setForm({ ...form, razorpay_mode: e.target.value })} className="field"><option value="test">Test</option><option value="live">Live</option></select></div>
            <div><label className="field-label">Enabled</label><select value={form.razorpay_enabled ? "1" : "0"} onChange={(e) => setForm({ ...form, razorpay_enabled: e.target.value === "1" })} className="field"><option value="0">No</option><option value="1">Yes</option></select></div>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary mt-6">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save Settings</>}</button>
      </div>
    </div>
  );
}
